import { memo, useCallback, useEffect, useMemo, useRef } from 'react';
import { t } from 'i18next';
import { MathUtils } from 'three';
import { Areas, Rectangle } from '@thive/fddm-types';

import { Dimensions } from '@/modules/common/components/Dimensions';
import { Accordion } from '@/modules/common/components/Accordion';
import { NumUtils } from '@/modules/common/utils';
import { clampAngle180 } from '@/modules/common/helpers/math';
import {
  areasToFlatCollection,
  isHighwayArea,
  pushAreaToItsCollection,
} from '@/modules/floorplanDesign/helpers';
import { useFloorplanDesignStore } from '@/modules/floorplanDesign/store/useFloorplanDesignStore';
import { createEmptyAreas } from '@/modules/floorplanDesign/helpers/helpers';
import { Panel_Display_Mode } from '@/modules/floorplanDesign/types';
import { useAppStore } from '@/modules/app/store/useAppStore';
import { FddmServiceFactory } from '@/modules/canvas/services/Floorplan/FddmServiceFactory';

type FloorplanDesignState = ReturnType<typeof useFloorplanDesignStore.getState>;

const getSelectedAreasCenter = (
  selectedIds: string[],
  floorplanState: FloorplanDesignState,
): Rectangle | null => {
  const selectedAreas = floorplanState.getAreasByIds(selectedIds);
  const areaCollection = areasToFlatCollection(selectedAreas).filter(
    (area) => !isHighwayArea(area),
  );

  if (areaCollection.length === 0) return null;
  if (areaCollection.length > 1) return null;

  const area = areaCollection[0];
  if (isHighwayArea(area)) {
    return null;
  }

  return { ...area.rectangle, angle: MathUtils.radToDeg(area.rectangle.angle) };
};

const useSelectedAreaDimension = (selectedIds: string[], value: keyof Rectangle): number | null =>
  useFloorplanDesignStore((state) => {
    const bounds = getSelectedAreasCenter(selectedIds, state);
    return bounds ? bounds[value] : null;
  });

const useAreaDimensions = () => {
  const panelModeState = useAppStore((state) => state.panelModeState);
  const selectedIds = useAppStore((state) => state.selectedIds);
  const fddmSvc = useMemo(() => FddmServiceFactory.getInstance().createFddmService(), []);

  const centerX = useSelectedAreaDimension(selectedIds, 'centerX');
  const centerY = useSelectedAreaDimension(selectedIds, 'centerY');
  const length = useSelectedAreaDimension(selectedIds, 'length');
  const width = useSelectedAreaDimension(selectedIds, 'width');
  const angle = useSelectedAreaDimension(selectedIds, 'angle');

  const dimensions = useMemo(
    () =>
      centerX === null || centerY === null || length === null || width === null || angle === null
        ? null
        : { centerX, centerY, length, width, angle },
    [centerX, centerY, length, width, angle],
  );

  const currentDimensionsRef = useRef<{
    x: number;
    y: number;
    length: number;
    width: number;
    angle: number;
  } | null>(null);

  useEffect(() => {
    currentDimensionsRef.current = dimensions
      ? {
          x: dimensions.centerX,
          y: dimensions.centerY,
          length: dimensions.length,
          width: dimensions.width,
          angle: dimensions.angle,
        }
      : null;
  }, [dimensions]);

  const getSelectedAreas = useCallback(() => {
    const floorplanState = useFloorplanDesignStore.getState();
    return floorplanState.getAreasByIds(selectedIds);
  }, [selectedIds]);

  const onXChange = useCallback(
    (newValue: number) => {
      const newX = NumUtils.mmToMetersRoundedToMm(newValue);
      const selectedAreas = getSelectedAreas();
      const areaCollection = areasToFlatCollection(selectedAreas);

      if (areaCollection.length === 1 && !isHighwayArea(areaCollection[0])) {
        const updates: Areas = {
          highwayAreas: selectedAreas.highwayAreas,
          rackingAreas: selectedAreas.rackingAreas.map((area) => ({
            ...area,
            rectangle: { ...area.rectangle, centerX: newX },
          })),
          singularStationAreas: selectedAreas.singularStationAreas.map((area) => ({
            ...area,
            rectangle: { ...area.rectangle, centerX: newX },
          })),
          stationaryAreas: selectedAreas.stationaryAreas.map((area) => ({
            ...area,
            rectangle: { ...area.rectangle, centerX: newX },
          })),
          manualAreas: selectedAreas.manualAreas.map((area) => ({
            ...area,
            rectangle: { ...area.rectangle, centerX: newX },
          })),
        };

        fddmSvc.updateAreas(updates);
        return;
      }

      if (!currentDimensionsRef.current) return;

      const delta = newX - currentDimensionsRef.current.x;

      const updates: Areas = {
        highwayAreas: selectedAreas.highwayAreas,
        rackingAreas: selectedAreas.rackingAreas.map((area) => ({
          ...area,
          rectangle: { ...area.rectangle, centerX: area.rectangle.centerX + delta },
        })),
        singularStationAreas: selectedAreas.singularStationAreas.map((area) => ({
          ...area,
          rectangle: { ...area.rectangle, centerX: area.rectangle.centerX + delta },
        })),
        stationaryAreas: selectedAreas.stationaryAreas.map((area) => ({
          ...area,
          rectangle: { ...area.rectangle, centerX: area.rectangle.centerX + delta },
        })),
        manualAreas: selectedAreas.manualAreas.map((area) => ({
          ...area,
          rectangle: { ...area.rectangle, centerX: area.rectangle.centerX + delta },
        })),
      };

      fddmSvc.updateAreas(updates);
    },
    [fddmSvc, getSelectedAreas],
  );

  const onYChange = useCallback(
    (newValue: number) => {
      const newY = NumUtils.mmToMetersRoundedToMm(newValue);
      const selectedAreas = getSelectedAreas();
      const areaCollection = areasToFlatCollection(selectedAreas);

      if (areaCollection.length === 1 && !isHighwayArea(areaCollection[0])) {
        const updates: Areas = {
          highwayAreas: selectedAreas.highwayAreas,
          rackingAreas: selectedAreas.rackingAreas.map((area) => ({
            ...area,
            rectangle: { ...area.rectangle, centerY: newY },
          })),
          singularStationAreas: selectedAreas.singularStationAreas.map((area) => ({
            ...area,
            rectangle: { ...area.rectangle, centerY: newY },
          })),
          stationaryAreas: selectedAreas.stationaryAreas.map((area) => ({
            ...area,
            rectangle: { ...area.rectangle, centerY: newY },
          })),
          manualAreas: selectedAreas.manualAreas.map((area) => ({
            ...area,
            rectangle: { ...area.rectangle, centerY: newY },
          })),
        };

        fddmSvc.updateAreas(updates);
        return;
      }

      if (!currentDimensionsRef.current) return;

      const delta = newY - currentDimensionsRef.current.y;

      const updates: Areas = {
        highwayAreas: selectedAreas.highwayAreas,
        rackingAreas: selectedAreas.rackingAreas.map((area) => ({
          ...area,
          rectangle: { ...area.rectangle, centerY: area.rectangle.centerY + delta },
        })),
        singularStationAreas: selectedAreas.singularStationAreas.map((area) => ({
          ...area,
          rectangle: { ...area.rectangle, centerY: area.rectangle.centerY + delta },
        })),
        stationaryAreas: selectedAreas.stationaryAreas.map((area) => ({
          ...area,
          rectangle: { ...area.rectangle, centerY: area.rectangle.centerY + delta },
        })),
        manualAreas: selectedAreas.manualAreas.map((area) => ({
          ...area,
          rectangle: { ...area.rectangle, centerY: area.rectangle.centerY + delta },
        })),
      };

      fddmSvc.updateAreas(updates);
    },
    [fddmSvc, getSelectedAreas],
  );

  const onLengthChange = useCallback(
    (newValue: number) => {
      const newValueSanitized = NumUtils.mmToMetersRoundedToMm(newValue);
      const selectedAreas = getSelectedAreas();
      const areas = areasToFlatCollection(selectedAreas);

      if (areas.length !== 1 || isHighwayArea(areas[0])) return;

      const area = areas[0];
      const newArea = { ...area, rectangle: { ...area.rectangle, length: newValueSanitized } };
      const updates: Areas = createEmptyAreas();
      pushAreaToItsCollection(updates, newArea);
      fddmSvc.updateAreas(updates);
    },
    [fddmSvc, getSelectedAreas],
  );

  const onWidthChange = useCallback(
    (newValue: number) => {
      const newValueSanitized = NumUtils.mmToMetersRoundedToMm(newValue);
      const selectedAreas = getSelectedAreas();
      const areas = areasToFlatCollection(selectedAreas);

      if (areas.length !== 1 || isHighwayArea(areas[0])) return;

      const area = areas[0];
      const newArea = { ...area, rectangle: { ...area.rectangle, width: newValueSanitized } };
      const updates: Areas = createEmptyAreas();
      pushAreaToItsCollection(updates, newArea);
      fddmSvc.updateAreas(updates);
    },
    [fddmSvc, getSelectedAreas],
  );

  const onAngleChange = useCallback(
    (newValue: number) => {
      const newValueSanitized = MathUtils.degToRad(clampAngle180(newValue));
      const selectedAreas = getSelectedAreas();
      const areas = areasToFlatCollection(selectedAreas);

      if (areas.length !== 1 || isHighwayArea(areas[0])) return;

      const area = areas[0];
      const newArea = { ...area, rectangle: { ...area.rectangle, angle: newValueSanitized } };
      const updates: Areas = createEmptyAreas();
      pushAreaToItsCollection(updates, newArea);
      fddmSvc.updateAreas(updates);
    },
    [fddmSvc, getSelectedAreas],
  );

  return {
    dimensions,
    onXChange,
    onYChange,
    onLengthChange,
    onWidthChange,
    onAngleChange,
    panelModeState,
  };
};

function AreaDimensionsComponent() {
  const {
    dimensions,
    onXChange,
    onYChange,
    onLengthChange,
    onWidthChange,
    onAngleChange,
    panelModeState,
  } = useAreaDimensions();

  const mode = useAppStore((state) => state.mode);
  const isReadOnly = mode !== 'EDITABLE';

  const templateModeDisabled =
    panelModeState === Panel_Display_Mode.TEMPLATE ||
    panelModeState === Panel_Display_Mode.TEMPLATE_LASSIE ||
    panelModeState === Panel_Display_Mode.TEMPLATE_LASSIE_MINI ||
    panelModeState === Panel_Display_Mode.TEMPLATE_SHUTTLE_RACK ||
    panelModeState === Panel_Display_Mode.TEMPLATE_HIK;

  const disabled = isReadOnly || templateModeDisabled;

  const angleDisabled =
    panelModeState !== Panel_Display_Mode.SINGULAR_STATION &&
    panelModeState !== Panel_Display_Mode.STATIONARY &&
    panelModeState !== Panel_Display_Mode.RACKING;

  const sizeDisabled = angleDisabled;

  if (!dimensions) return null;

  return (
    <Accordion
      title={t<string>('interface:properties.dimensions.label', 'Dimensions')}
      unmountOnExit
      defaultExpanded
      sx={{ px: 2 }}
    >
      <Dimensions
        disabled={disabled}
        disableLength={sizeDisabled}
        disableWidth={sizeDisabled}
        disableAngle={angleDisabled}
        x={NumUtils.metersToMmRounded(dimensions.centerX)}
        onXChange={onXChange}
        y={NumUtils.metersToMmRounded(dimensions.centerY)}
        onYChange={onYChange}
        length={NumUtils.metersToMmRounded(dimensions.length)}
        onLengthChange={onLengthChange}
        width={NumUtils.metersToMmRounded(dimensions.width)}
        onWidthChange={onWidthChange}
        angle={dimensions.angle}
        onAngleChange={onAngleChange}
      />
    </Accordion>
  );
}

export const AreaDimensions = memo(AreaDimensionsComponent);
